/**
 * LogoutEverywhereModal Component
 *
 * Confirmation dialog before revoking all sessions across all devices.
 * Shows device count for context and requires explicit confirmation.
 */

// 1. External dependencies
import React from 'react';
import { LogOut } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button
} from '@enfomentis/ui';

export interface LogoutEverywhereModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed without confirming */
  onClose: () => void;
  /** Callback when user confirms the action */
  onConfirm: () => void;
  /** Number of registered devices — shown in the body for context */
  deviceCount: number;
  /** Whether the logout operation is in progress */
  isLoading: boolean;
}

/**
 * Logout Everywhere Confirmation Modal
 *
 * Informs the user how many sessions will be ended and requires
 * an explicit button press before proceeding. Cancel is disabled
 * while the operation is in flight.
 */
export const LogoutEverywhereModal: React.FC<LogoutEverywhereModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  deviceCount,
  isLoading
}) => {
  const bodyText =
    deviceCount > 1
      ? `This will immediately end all ${deviceCount} active sessions, including this one. You will be redirected to the login page.`
      : 'This will sign you out of your current session. You will be redirected to the login page.';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isLoading}
      hideCloseButton={isLoading}
      placement="top"
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <LogOut className="w-5 h-5 text-danger" />
          <span>Sign out everywhere?</span>
        </ModalHeader>

        <ModalBody>
          <p className="text-sm text-default-600">{bodyText}</p>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="flat"
            onPress={onClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            variant="solid"
            onPress={onConfirm}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {isLoading ? 'Signing out...' : 'Sign out everywhere'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
